import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel, openPanel } from '@state/actions/panelsActions'
import { useItemSettings } from '@hooks/useItemSettings'
import { uploadImage } from '@helpers/api'
import { useCreateBook } from '@hooks/mutations/useCreateBook'
import { useBooks } from '@hooks/useBooks'
import Button from '@components/Button'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Heading from '@components/Heading'
import GridColumns from '@components/GridColumns'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
  LABELS,
  USER_ROLES,
} from '@helpers/constants'
import {
  setCurrentEditBookId,
  setEditBookPanelStartOnTab,
} from '@state/actions/appActions'
import { removeDuplicates } from '@helpers/utilities'
import { useSections } from '@hooks/useSections'
import { setSubjectFilter } from '@state/actions/filtersActions'
import { useUpdateBook } from '@hooks/mutations/useUpdateBook'

const CreateBookPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { userId, userRole, currentAddSectionToBookObject },
  } = state

  const { schoolYears, subjectOptions, levelOptions, tags } = useItemSettings()
  const { userCreatedSections, publicSections } = useSections()

  const subjectsInSections = removeDuplicates(
    [...userCreatedSections, ...publicSections].map(
      (section) => section.subject?.id
    )
  )
  const availableSubjectOptions = subjectOptions
    .filter((subject) => subjectsInSections.includes(subject.value))
    .map((option) => ({
      ...option,
      disabled:
        currentAddSectionToBookObject &&
        currentAddSectionToBookObject.subject?.id !== option.value,
    }))
  const [createdBookId, setCreatedBookId] = useState(null)
  const [createdBookSubject, setCreatedBookSubject] = useState(null)
  const { post: postUpdateBook } = useUpdateBook()

  useEffect(() => {
    if (createdBookId && currentAddSectionToBookObject) {
      postUpdateBook(
        {
          id: createdBookId,
          connectSections: [currentAddSectionToBookObject.id],
        },
        `Avsnittet ${currentAddSectionToBookObject.name} tillagt.`,
        'Nåt gick fel när avsnittet skulle läggas till.'
      )

      dispatch(setCurrentEditBookId(createdBookId))

      dispatch(closePanel(PANELS.CREATE_BOOK))

      dispatch(setEditBookPanelStartOnTab(LABELS.EDIT_SECTIONS))
      dispatch(openPanel(PANELS.EDIT_BOOK))
    }
  }, [createdBookId])

  const { post, isPosting } = useCreateBook()

  const { register, handleSubmit, errors, control, formState } = useForm(
    currentAddSectionToBookObject
      ? {
          defaultValues: {
            subject: currentAddSectionToBookObject.subject?.id,
          },
          mode: 'onChange',
        }
      : { mode: 'onChange' }
  )

  const { isValid } = formState

  const onSubmit = async (data) => {
    let fileName = ''

    if (data?.image?.length > 0) {
      // We have image, upload it!
      try {
        fileName = await uploadImage(data.image[0])
      } catch (error) {
        console.log(error)
        // show toast here?
      }
    }

    const tagIdsToConnect = data?.tags?.reduce((acc, tag) => {
      if (tag.hasOwnProperty('id')) {
        return [...acc, tag.id]
      } else {
        return acc
      }
    }, [])

    const tagsToCreate = data?.tags?.reduce((acc, tag) => {
      if (!tag.hasOwnProperty('id')) {
        return [...acc, { name: tag.name }]
      } else {
        return acc
      }
    }, [])

    const res = await post({
      name: data.name,
      description: data.description,
      subject: data.subject,
      schoolYear: data.schoolYear,
      level: data.level,
      creator: userId,
      connectTags: tagIdsToConnect,
      createTags: tagsToCreate,
      image: fileName,
    })

    if (res.success && res.id) {
      // This triggers useEffect needed to not have stale data
      // when getting newly created book from useBooks hook
      setCreatedBookSubject(data.subject)
      setCreatedBookId(res.id)
    }
  }

  return (
    <>
      <div className='flex justify-end'>
        <Button
          handleClick={() => {
            dispatch(closePanel(PANELS.CREATE_BOOK))
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        ></Button>
      </div>

      {createdBookId && !currentAddSectionToBookObject && (
        <div className='mx-auto mt-[25%] h-full max-w-[260px] text-center'>
          <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.LARGE}>
            Lägg till avsnitt i boken.
          </Heading>

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Button
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            iconLeft
            icon={'Section'}
            to={'/bibliotek/avsnitt'}
            handleClick={() => {
              dispatch(setSubjectFilter(createdBookSubject))
              dispatch(closePanel(PANELS.CREATE_BOOK))
            }}
          >
            Välj avsnitt
          </Button>
        </div>
      )}

      {!createdBookId && (
        <>
          <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
            Skapa en bok
          </Heading>

          <Divider size={DIVIDER_SIZE.BASE} />

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Label>Välj årskurs*</Form.Label>
            {schoolYears.map((schoolYear, index) => {
              const { id, name } = schoolYear
              return (
                <Form.Radio
                  key={`schoolYear${index}`}
                  name={'schoolYear'}
                  id={name}
                  ref={register({ required: true })}
                  label={`Årskurs ${name}`}
                  value={id}
                />
              )
            })}
            {errors.schoolYear && (
              <Form.ErrorMessage>Årskurs behövs</Form.ErrorMessage>
            )}

            {userRole === USER_ROLES.ADMIN && (
              <>
                <Divider size={DIVIDER_SIZE.SMALL} />

                <Form.Label htmlFor='image'>Välj en bild</Form.Label>
                <Form.ImageUpload name='image' id='image' ref={register()} />
              </>
            )}

            <Divider size={DIVIDER_SIZE.SMALL} />

            <GridColumns cols={2}>
              <div>
                <Form.Label htmlFor='subject'>Ämne*</Form.Label>
                <Form.Select
                  name='subject'
                  id='subject'
                  ref={register({ required: true })}
                  options={[
                    { value: '', name: 'Välj...' },
                    ...availableSubjectOptions,
                  ]}
                />
                {errors.subject && (
                  <Form.ErrorMessage>Ämne behövs</Form.ErrorMessage>
                )}
              </div>
              <div>
                <Form.Label htmlFor='level'>Nivå*</Form.Label>
                <Form.Select
                  name='level'
                  id='level'
                  ref={register({ required: true })}
                  options={[{ value: '', name: 'Välj...' }, ...levelOptions]}
                />
                {errors.level && (
                  <Form.ErrorMessage>Nivå behövs</Form.ErrorMessage>
                )}
              </div>
            </GridColumns>

            <Divider size={DIVIDER_SIZE.SMALL} />

            <Form.Label htmlFor='name'>Namnge boken*</Form.Label>
            <Form.Input
              type={'text'}
              name='name'
              id='name'
              ref={register({ required: true })}
              placeholder='Titel'
            />
            {errors.name && <Form.ErrorMessage>Namn behövs</Form.ErrorMessage>}

            <Divider size={DIVIDER_SIZE.SMALL} />

            <Form.Label htmlFor='name'>Beskriv boken</Form.Label>
            <Controller
              control={control}
              name='description'
              render={(
                { onChange, onBlur, value, name, ref },
                { invalid, isTouched, isDirty }
              ) => (
                <Form.Editor
                  handleChange={(value) => onChange(value)}
                  elementId={`create-book-description`}
                  // placeholder='Beskrivning'
                />
              )}
            />

            <Divider size={DIVIDER_SIZE.SMALL} />

            {userRole === USER_ROLES.ADMIN && (
              <>
                <Form.Label htmlFor='tags'>Lägg till taggar</Form.Label>
                <Controller
                  control={control}
                  name={'tags'}
                  defaultValue={[]}
                  render={(
                    { onChange, onBlur, value, name, ref },
                    { invalid, isTouched, isDirty }
                  ) => (
                    <Form.Tags
                      onChange={useCallback((tags) => onChange(tags), [])}
                      tags={[]}
                      suggestions={tags}
                    />
                  )}
                />
                <Divider size={DIVIDER_SIZE.SMALL} />
              </>
            )}

            <ButtonWithLoader
              type={BUTTON_TYPE.PRIMARY}
              isLoading={isPosting}
              disabledText={'Skapa bok'}
              enabledText={'Skapa bok'}
              disabled={!isValid || isPosting}
            />
          </Form>
        </>
      )}
    </>
  )
}

export default CreateBookPanel
